import React, { useState } from 'react';

import { DotIcon, PencilIcon, PlusCircleIcon } from 'lucide-react';

import { Card, CardContent, CardHeader } from '@sandtech-hub-monorepo/ui-kit';

import {
  LanguageEnum,
  LanguageProficiencyLevel,
  LanguageSkill,
  PublicLanguageSkill,
} from '@sandtech-hub-monorepo/ehub-client';
import { LanguagesSectionEditDialog } from '../Edit/LanguagesSectionEditDialog';
import { ProfileCard } from './ProfileCard';
import ProfileEmptySection from './ProfileEmptySection';
import type { EmptyStateStyling } from './ProfileEmptySection';

interface EmptyStateContent {
  header: string;
  main: string;
  callToActionText: string;
  icon: JSX.Element;
}

interface Props {
  editable?: boolean;
  languages: LanguageSkill[] | PublicLanguageSkill[];
  /**
   * Renders a full empty state (Figma 10708-66006) when the list is empty.
   * Omitted by central-hub, which renders nothing for an empty list - only
   * the header's plus icon changes there, unchanged from before this prop.
   */
  emptyState?: EmptyStateContent;
  /** Empty-state visual overrides; omitted by central-hub. */
  emptyStateStyling?: EmptyStateStyling;
}

export const LanguagesSection = ({
  editable,
  languages,
  emptyState,
  emptyStateStyling,
}: Props) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  return (
    <>
      <ProfileCard
        title="Languages"
        className="flex-1"
        editable={editable}
        plusInsteadOfPencil={!languages.length}
        action={() => setEditDialogOpen(true)}
      >
        {!languages.length && emptyState ? (
          <ProfileEmptySection
            header={emptyState.header}
            main={emptyState.main}
            callToActionText={emptyState.callToActionText}
            callToAction={() => setEditDialogOpen(true)}
            icon={emptyState.icon}
            {...emptyStateStyling}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {languages.map((lang) => (
              <div className="inline-flex" key={lang.id}>
                <span className="font-semibold">
                  {
                    Object.keys(LanguageEnum)[
                      Object.values(LanguageEnum).indexOf(lang.language)
                    ]
                  }
                </span>
                <DotIcon className="min-w-min" />
                <span className="w-full overflow-hidden text-ellipsis whitespace-nowrap">
                  {
                    Object.keys(LanguageProficiencyLevel)[
                      Object.values(LanguageProficiencyLevel).indexOf(
                        lang.proficiencyLevel
                      )
                    ]
                  }
                </span>
              </div>
            ))}
          </div>
        )}
      </ProfileCard>
      {editable && (
        <LanguagesSectionEditDialog
          defaultValues={languages}
          open={editDialogOpen}
          handleOpenChange={(open) => setEditDialogOpen(open)}
        />
      )}
    </>
  );
};
